import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { createEvaluationTestQueue, isTestRateLimitError, type TestQueueState } from "~/utils/evaluation-test-queue";
import { isTestTimeout } from "~/utils/evaluation-test-wait";

export const useEvaluationTestQueue = defineStore("evaluation-test-queue", () => {
  const state = ref<TestQueueState>({ active: 0, queued: 0, pausedUntil: 0 });
  const pauseReason = ref("");
  const activeTests = ref<{ id: number; label: string; stage: string; startedAt: number }[]>([]);
  let nextId = 0;
  const queue = createEvaluationTestQueue((next) => { state.value = next; });
  const limits = ref(queue.configure(2, 1_000));
  const concurrency = computed({
    get: () => limits.value.concurrency,
    set: (value: number) => { limits.value = queue.configure(value, limits.value.startIntervalMs); },
  });
  const startIntervalSeconds = computed({
    get: () => limits.value.startIntervalMs / 1_000,
    set: (value: number) => { limits.value = queue.configure(limits.value.concurrency, Number(value) * 1_000); },
  });

  const run = <T>(task: (setStage: (stage: string) => void) => Promise<T>, label = "Test"): Promise<T> => queue.add(async () => {
    const id = nextId++;
    activeTests.value.push({ id, label, stage: "Waiting for Automark to accept the test", startedAt: Date.now() });
    try {
      const result = await task((stage) => {
        const active = activeTests.value.find((test) => test.id === id);
        if (active) active.stage = stage;
      });
      if (isTestRateLimitError(result)) {
        pauseReason.value = "Rate limit encountered";
        queue.pause();
      }
      return result;
    } catch (error) {
      if (isTestRateLimitError(error) || isTestTimeout(error)) {
        pauseReason.value = isTestTimeout(error) ? "Test timed out" : "Rate limit encountered";
        queue.pause();
      }
      throw error;
    } finally {
      activeTests.value = activeTests.value.filter((test) => test.id !== id);
    }
  });

  return { concurrency, startIntervalSeconds, state, pauseReason, activeTests, run };
});
